import {firstValueFrom} from 'rxjs';
import {IBaseCRUDComponent} from '../../../../sdk/src/components/ibase-crud/ibase-crud.component';
import {Component} from '@angular/core';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {EDeleteMode} from '../../../../sdk/src/enums/delete-mode.enum';
import {CpmCS3BucketApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-s3-bucket.api-model';
import {
    NewEditS3BucketModalComponent,
    NewEditS3BucketModalConfig,
    NewEditS3BucketModalResult
} from '../../modals/new-edit-s3-bucket-modal/new-edit-s3-bucket-modal.component';
import {CpmCS3BucketApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-s3-bucket.api-service';
import {ETableColFilterType} from '../../../../sdk/src/components/generic-table/table-col-def';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';

/**
 * S3Bucket CRUD table component
 */
@Component({
    selector: 'app-s3-bucket-crud',
    templateUrl: './s3-bucket-crud.component.html',
    imports: [PrimeNgImportsModule, SdkImportsModule]
})
export class S3BucketCRUDComponent extends IBaseCRUDComponent<
    CpmCS3BucketApiModel,
    NewEditS3BucketModalComponent,
    NewEditS3BucketModalConfig,
    NewEditS3BucketModalResult
> {
    protected readonly CpmCS3BucketApiService = CpmCS3BucketApiService;
    // ===============================================================
    // ===============================================================

    protected helpVisible = false;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure({
            // Model config
            modelName: 'S3BucketApiModel',
            getModelHumanIdentifier: (model: CpmCS3BucketApiModel) => model.name ?? '<Unknown OO8 Connector>',

            // Service config
            deleteMode: EDeleteMode.CONFIRM_REENFORCED,
            deleteModelService: (modelToDelete: CpmCS3BucketApiModel) =>
                CpmCS3BucketApiService.deleteS3Bucket(modelToDelete.uuid!),

            // Modal config
            modalType: NewEditS3BucketModalComponent,
            getDialogServiceParameters: NewEditS3BucketModalComponent.getDialogServiceParameters,
            modalExtendedConfigBuilder: (model?: CpmCS3BucketApiModel) => new NewEditS3BucketModalConfig(model),

            // Authorization config
            authorizationNew: CpmCS3BucketApiService.endpointNew(),
            authorizationEdit: CpmCS3BucketApiService.endpointEdit(this.FAKE_UUID),
            authorizationDelete: CpmCS3BucketApiService.endpointDelete(this.FAKE_UUID)
        });

        this.modelHumanName = 'S3 Bucket';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onReady(): Promise<void> {}
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On refresh (Error handling is done in the parent)
     */
    public override async onRefresh(): Promise<void> {
        this.loadingStr = 'Loading S3 Buckets...';

        const result = await firstValueFrom(CpmCS3BucketApiService.getS3BucketList({}, this.paginatorConfig));

        this.items = result.data;
        this.totalItemCount = result.total;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On help button pressed
     */
    public override async onHelp(): Promise<void> {
        this.helpVisible = true;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected override async initColumns(): Promise<void> {
        this.tableCols = [
            {
                field: 'name',
                header: 'Name',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'api_endpoint',
                header: 'API Endpoint',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'region',
                header: 'Region',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'bucket',
                header: 'Bucket',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            }
        ];

        this.addCreatedColumns();
    }
}
